import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, Target, BarChart3, ArrowLeft, TrendingUp, Download } from "lucide-react";
import { useAssetAllocation } from "@/hooks/useAssetAllocation";
import { usePerformanceData } from "@/hooks/usePerformanceData";
import { useFactsheetDownload } from "@/hooks/useFactsheetDownload";
import { usePortfolioContent } from "@/hooks/usePortfolioContent";
import { PortfolioOverviewSection, ExpectedPerformanceSection, PerformanceTableHeader, PerformanceTableRow, BenchmarkFooter } from "@/components/PortfolioPageSections";
import { Skeleton } from "@/components/ui/skeleton";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const R3Portfolio = () => {
  const { allocation, loading, error } = useAssetAllocation('R3');
  const { performance, loading: perfLoading, error: perfError } = usePerformanceData('R3');
  const { downloadFactsheet, loading: downloadLoading } = useFactsheetDownload();
  const { content: portfolioContent, benchmarks, loading: contentLoading, error: contentError } = usePortfolioContent('R3');
  
  // Process allocation data to combine categories and rename
  const processedAllocation = allocation.reduce((acc, item) => {
    const categoryLower = item.category.toLowerCase();
    
    // Combine GCC Equities and Equities into Other Equities
    if (categoryLower === 'gcc equities' || categoryLower === 'equities' || categoryLower === 'international equities') {
      const otherEquitiesIndex = acc.findIndex(a => a.category === 'Other Equities');
      if (otherEquitiesIndex !== -1) {
        acc[otherEquitiesIndex].percentage = parseFloat((acc[otherEquitiesIndex].percentage + item.percentage).toFixed(2));
      } else {
        acc.push({
          ...item,
          category: 'Other Equities',
          percentage: parseFloat(item.percentage.toFixed(2))
        });
      }
    }
    // Combine Fixed Interest into Global Fixed Interest
    else if (categoryLower === 'fixed interest') {
      const globalFixedIndex = acc.findIndex(a => a.category === 'Global Fixed Interest');
      if (globalFixedIndex !== -1) {
        acc[globalFixedIndex].percentage = parseFloat((acc[globalFixedIndex].percentage + item.percentage).toFixed(2));
      } else {
        acc.push({
          ...item,
          category: 'Global Fixed Interest',
          percentage: parseFloat(item.percentage.toFixed(2))
        });
      }
    }
    // Combine Unknown under Other
    else if (categoryLower === 'unknown') {
      const otherIndex = acc.findIndex(a => 
        a.category.toLowerCase() === 'others' || a.category.toLowerCase() === 'other'
      );
      
      if (otherIndex !== -1) {
        acc[otherIndex].percentage = parseFloat((acc[otherIndex].percentage + item.percentage).toFixed(2));
      } else {
        acc.push({
          ...item,
          category: 'Other',
          percentage: parseFloat(item.percentage.toFixed(2))
        });
      }
    }
    // Rename "Others" to "Other" for consistency
    else if (categoryLower === 'others') {
      acc.push({
        ...item,
        category: 'Other',
        percentage: parseFloat(item.percentage.toFixed(2))
      });
    }
    // Keep other categories as-is
    else {
      acc.push({
        ...item,
        percentage: parseFloat(item.percentage.toFixed(2))
      });
    }
    return acc;
  }, [] as typeof allocation);
  
  // Sort allocation data by percentage in descending order
  const sortedAllocation = [...processedAllocation].sort((a, b) => b.percentage - a.percentage);
  
  // Colors for the pie chart
  const pieColors = [
    '#3b82f6', // blue-500
    '#10b981', // emerald-500  
    '#f59e0b', // amber-500
    '#ef4444', // red-500
    '#8b5cf6', // violet-500
    '#06b6d4', // cyan-500
    '#84cc16', // lime-500
    '#f97316', // orange-500
    '#ec4899', // pink-500
    '#6366f1', // indigo-500
    '#14b8a6', // teal-500
    '#f59e0b', // amber-500
  ];

  // Prepare data for pie chart - only show items with >1% for cleaner visualization
  const pieData = sortedAllocation
    .filter(item => item.percentage > 1)
    .map((item, index) => ({
      name: item.category,
      value: item.percentage,
      color: pieColors[index % pieColors.length]
    }));
  return (
    <div className="py-8">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-primary-foreground text-primary">R Range - Balanced</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {contentLoading ? 'R3 Portfolio' : (portfolioContent?.title || 'R3 Portfolio')}
            </h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto mb-4">
              {contentLoading ? 'Balanced Growth Strategy' : (portfolioContent?.subtitle || 'Balanced Growth Strategy')}
            </p>
            <div className="flex flex-col items-center gap-4 text-lg">
              <div>
                <span className="font-semibold">Target Audience:</span> Balanced
              </div>
              <Button 
                variant="outline" 
                onClick={() => downloadFactsheet('R3')}
                disabled={downloadLoading}
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/20"
              >
                <Download className="h-4 w-4 mr-2" />
                {downloadLoading ? 'Downloading...' : 'Download Factsheet'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="outline" asChild className="mb-8">
          <Link to="/portfolios/r">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to R Range
          </Link>
        </Button>

        {/* Portfolio Overview */}
        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Portfolio Overview</h2>
              <p className="text-lg text-muted-foreground mb-6">
                The R3 portfolio represents a balanced approach within the OEIC-only framework, 
                offering equal weightings between equity and fixed income investments. This strategy 
                aims to capture moderate growth while maintaining the cost efficiency and simplicity 
                of the R Range philosophy.
              </p>
              <p className="text-muted-foreground mb-6">
                Ideal for investors seeking a balanced risk/return profile with the operational 
                simplicity of OEIC-only holdings, maintaining tight tracking to benchmarks while 
                providing meaningful diversification.
              </p>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Key Characteristics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Balanced OEIC allocation
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Moderate risk profile
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Global diversification
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Passive fund focus
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Sectoral allocation within 2% of benchmark
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Performance Metrics */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Expected Performance Characteristics</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">5-8%</CardTitle>
                <CardDescription>Expected Annual Return</CardDescription>
              </CardHeader>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">4-8%</CardTitle>
                <CardDescription>Annual Volatility</CardDescription>
              </CardHeader>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">±2%</CardTitle>
                <CardDescription>Sectoral Allocation</CardDescription>
              </CardHeader>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Balanced</CardTitle>
                <CardDescription>Target Audience</CardDescription>
              </CardHeader>
            </Card>
          </div>
        </section>

        {/* Performance Table */}
        <Card className="mb-16">
          <CardHeader>
            <CardTitle>Performance Summary</CardTitle>
            <CardDescription>Historical performance data for R3 Portfolio</CardDescription>
          </CardHeader>
          <CardContent>
            {perfLoading ? (
              <Skeleton className="h-48" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-muted">
                      <th className="text-left py-3 px-4 font-semibold">Period</th>
                      <th className="text-right py-3 px-4 font-semibold">Portfolio Return</th>
                      <th className="text-right py-3 px-4 font-semibold">CBAM III Benchmark</th>
                      <th className="text-right py-3 px-4 font-semibold">AFI Balanced</th>
                      <th className="text-right py-3 px-4 font-semibold">Excess vs Int. BM</th>
                      <th className="text-right py-3 px-4 font-semibold">Excess vs. AFI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.length > 0 ? (
                      performance.map((perf, index) => (
                        <tr key={index} className="border-b border-muted/50 hover:bg-muted/30">
                          <td className="py-3 px-4 font-medium">{perf.period}</td>
                          <td className="text-right py-3 px-4">
                            <span className={perf.portfolioReturn !== '--' ? 'font-semibold' : 'text-muted-foreground'}>
                              {perf.portfolioReturn}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4">
                            <span className={perf.benchmarkReturn !== '--' ? 'font-semibold' : 'text-muted-foreground'}>
                              {perf.benchmarkReturn}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4">
                            <span className={perf.benchmark2Return && perf.benchmark2Return !== '--' ? 'font-semibold' : 'text-muted-foreground'}>
                              {perf.benchmark2Return || '--'}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4">
                            <span className={`font-semibold ${
                              perf.excessReturn !== '--' && parseFloat(perf.excessReturn) > 0 
                                ? 'text-green-600' 
                                : perf.excessReturn !== '--' && parseFloat(perf.excessReturn) < 0
                                ? 'text-red-600'
                                : 'text-muted-foreground'
                            }`}>
                              {perf.excessReturn}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4">
                            <span className={`font-semibold ${
                              perf.excess2Return && perf.excess2Return !== '--' && parseFloat(perf.excess2Return) > 0
                                ? 'text-green-600'
                                : perf.excess2Return && perf.excess2Return !== '--' && parseFloat(perf.excess2Return) < 0
                                ? 'text-red-600'
                                : 'text-muted-foreground'
                            }`}>
                              {perf.excess2Return || '--'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <>
                        <tr className="border-b border-muted/50">
                          <td className="py-3 px-4">1 Month</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                        </tr>
                        <tr className="border-b border-muted/50">
                          <td className="py-3 px-4">3 Months</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                        </tr>
                        <tr className="border-b border-muted/50">
                          <td className="py-3 px-4">Year to Date</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                        </tr>
                        <tr className="border-b border-muted/50">
                          <td className="py-3 px-4">1 Year</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                        </tr>
                        <tr className="border-b border-muted/50">
                          <td className="py-3 px-4">3 Years</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4">Since Inception (Ann.)</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                          <td className="text-right py-3 px-4 text-muted-foreground">--</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-4">
              {perfError ? (
                <span className="text-amber-600">Note: {perfError}</span>
              ) : (
                "All performance figures shown are net of fees. Benchmark: CBAM III - AFI Balanced."
              )}
            </p>
          </CardContent>
        </Card>

        {/* Asset Allocation */}
        <section className="mb-16 bg-muted/50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-center mb-12">Asset Allocation</h2>
          
          {/* Detailed Asset Allocation Data */}
          <div className="mb-8 p-6 bg-background rounded-lg border">
            <h3 className="text-xl font-semibold mb-4">Detailed Asset Allocation</h3>
            {loading ? (
              <Skeleton className="h-20" />
            ) : (
              <div className="space-y-4">
                {sortedAllocation.length > 0 ? (
                  <>
                    <p className="text-muted-foreground mb-4">
                      Current allocation breakdown showing detailed asset distribution (sorted by allocation size):
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {sortedAllocation.slice(0, 8).map((item, index) => (
                        <div key={index} className="text-center p-3 bg-muted/30 rounded-lg">
                          <div className="text-2xl font-bold text-primary">{item.percentage.toFixed(2)}%</div>
                          <div className="text-sm font-medium">{item.category}</div>
                        </div>
                      ))}
                    </div>
                    {sortedAllocation.length > 8 && (
                      <p className="text-xs text-muted-foreground text-center mt-2">
                        Showing top 8 allocations. View detailed breakdown below.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-muted-foreground mb-2">
                      The R3 Portfolio follows a balanced investment strategy with the following target allocation:
                    </p>
                    <div className="grid grid-cols-2 gap-6 max-w-md mx-auto">
                      <div className="text-center p-4 bg-primary/5 rounded-lg border">
                        <div className="text-3xl font-bold text-primary">50.00%</div>
                        <div className="text-sm font-medium">Fixed Income</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Government & Corporate Bonds
                        </div>
                      </div>
                      <div className="text-center p-4 bg-primary/5 rounded-lg border">
                        <div className="text-3xl font-bold text-primary">50.00%</div>
                        <div className="text-sm font-medium">Equity</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Balanced Growth Holdings
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pie Chart Visualization */}
          {sortedAllocation.length > 0 && !loading && (
            <div className="mb-8 p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-4 text-center">Portfolio Allocation Chart</h3>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value.toFixed(2)}%`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [`${value.toFixed(2)}%`, 'Allocation']}
                      labelStyle={{ color: '#000' }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => <span style={{ color: '#374151' }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-4">
                Chart shows allocations greater than 1% for clarity. All allocations are listed in the detailed breakdown below.
              </p>
            </div>
          )}

          {/* Detailed Allocation Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Asset Allocation</CardTitle>
              <CardDescription>Complete breakdown of portfolio holdings</CardDescription>
            </CardHeader>
            <CardContent>
              {sortedAllocation.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-muted">
                        <th className="text-left py-3 px-4 font-semibold">Asset Category</th>
                        <th className="text-right py-3 px-4 font-semibold">Allocation %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAllocation.map((item, index) => (
                        <tr key={index} className="border-b border-muted/50 hover:bg-muted/30">
                          <td className="py-3 px-4 font-medium">{item.category}</td>
                          <td className="text-right py-3 px-4">
                            <span className="text-lg font-bold text-primary">{item.percentage.toFixed(2)}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-muted">
                        <th className="text-left py-3 px-4 font-semibold">Asset Category</th>
                        <th className="text-right py-3 px-4 font-semibold">Target %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">Large-cap Blend Equity</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">25.00%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">Government Bonds</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">20.00%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">Investment Grade Corporate Bonds</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">15.00%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">International Developed Markets</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">12.00%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">Mid-cap Equity</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">8.00%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">High Yield Bonds</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">8.00%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">Index-linked Bonds</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">5.00%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-muted/50">
                        <td className="py-3 px-4 font-medium">Emerging Markets</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">5.00%</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium">Cash Equivalents</td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-primary">2.00%</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-sm text-muted-foreground mt-4">
                {error ? (
                  <span className="text-amber-600">Note: {error}</span>
                ) : (
                  "Allocation percentages shown are based on the most recent portfolio data available."
                )}
              </p>
            </CardContent>
          </Card>
        </section>


        {/* CTA */}
        <Card className="text-center">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold mb-4">Interested in the R3 Portfolio?</h3>
            <p className="text-muted-foreground mb-6">
              Learn how the R3 balanced OEIC strategy can help achieve long-term growth with controlled costs.
            </p>
            <Button size="lg" asChild>
              <Link to="/contact">Schedule a Consultation</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default R3Portfolio;